import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Cinematic full-bleed hero slideshow. Crossfades between slides, either on a
 * timer or via the dots/arrows, and pauses while the user hovers or interacts.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement[]} slides
 * @property {HTMLElement[]} [dots]
 *
 * @extends {Component<Refs>}
 */
export class HeroCarousel extends Component {
  requiredRefs = ['slides'];

  /** @type {number | undefined} */
  #interval;

  /** @type {number | undefined} */
  #resumeTimer;

  #hovering = false;
  #current = 0;

  /** Milliseconds to wait after a manual interaction before autoplay resumes. */
  static RESUME_DELAY = 4000;

  connectedCallback() {
    super.connectedCallback();
    this.#updateActiveSlide(0);
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    clearTimeout(this.#resumeTimer);
  }

  get slides() {
    return this.refs.slides ?? [];
  }

  get autoplayInterval() {
    const raw = this.getAttribute('autoplay');
    if (raw === null) return undefined;

    const seconds = parseFloat(raw);
    return Number.isNaN(seconds) ? undefined : seconds * 1000;
  }

  get autoplay() {
    return Boolean(this.autoplayInterval) && this.slides.length > 1 && !prefersReducedMotion();
  }

  /**
   * Starts automatic slide playback, if autoplay is enabled.
   */
  play() {
    if (this.#interval || !this.autoplay) return;

    this.#interval = window.setInterval(() => this.next(), this.autoplayInterval);
  }

  /**
   * Clears the autoplay timer without marking the carousel as manually paused.
   */
  suspend() {
    window.clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event]
   */
  next(event) {
    event?.preventDefault();
    this.#interact();
    this.#updateActiveSlide((this.#current + 1) % this.slides.length);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event]
   */
  previous(event) {
    event?.preventDefault();
    this.#interact();
    this.#updateActiveSlide((this.#current - 1 + this.slides.length) % this.slides.length);
  }

  /**
   * Jumps to a specific slide.
   * @param {number} index
   * @param {Event} [event]
   */
  select(index, event) {
    event?.preventDefault();
    this.#interact();

    const target = Number(index);
    if (Number.isNaN(target) || target === this.#current) return;

    this.#updateActiveSlide(target);
  }

  handlePointerEnter() {
    this.#hovering = true;
    this.suspend();
  }

  handlePointerLeave() {
    this.#hovering = false;
    clearTimeout(this.#resumeTimer);
    this.play();
  }

  /**
   * Pauses for a manual interaction (dot/arrow), then resumes after a short delay
   * unless the user is still hovering.
   */
  #interact() {
    this.suspend();
    clearTimeout(this.#resumeTimer);

    this.#resumeTimer = window.setTimeout(() => {
      if (!this.#hovering) this.play();
    }, HeroCarousel.RESUME_DELAY);
  }

  /**
   * @param {number} index
   */
  #updateActiveSlide(index) {
    this.#current = index;

    this.slides.forEach((slide, i) => {
      const active = i === index;
      slide.setAttribute('aria-hidden', String(!active));

      const focusable = slide.querySelector('a, button');
      focusable?.setAttribute('tabindex', active ? '0' : '-1');
    });

    (this.refs.dots ?? []).forEach((dot, i) => {
      dot.setAttribute('aria-selected', String(i === index));
    });
  }
}

if (!customElements.get('hero-carousel-component')) {
  customElements.define('hero-carousel-component', HeroCarousel);
}
